import React, { useCallback, useMemo } from "react";
import { CollateralSecurityInfoRow } from "../../../../../../src/api/fmr1";
import { useAppDispatch, useAppSelector } from "../../../../../../src/app/store";
import { ColumnType } from "../../../../../components/ui/baseDataTable";
import {
  addCollateralInfoRow,
  deleteCollateralInfoRow,
  selectFMR1DraftCollateralInfoRows,
  updateCollateralInfoRow,
} from "../../../logic/frm1Slice";
import {
  generateRowId,
  getInsertedSno,
  getInsertedSortOrder,
  getSnoUpdates,
  sortRowsBySortOrder,
  YES_NO_OPTIONS,
} from "../common/utils";
import { FMRTable, FMRTableColumn } from "../common/FMRTable";

export type CollateralSecurityDetailsTableHandle = {
  getDirtyRows: () => CollateralSecurityInfoRow[];
  getDirtyUpsertPayload: () => any[];
  getAllRows: () => CollateralSecurityInfoRow[];
  markAllClean: () => void;
  resetRows: (nextRows: CollateralSecurityInfoRow[]) => void;
};

export interface CollateralSecurityDetailsTableProps {
  onDirtyChange?: (isDirty: boolean) => void;
  onDirtyUpsertPayloadChange?: (payload: any[]) => void;
  initialRows?: CollateralSecurityInfoRow[];
}


const buildEmptyRow = (): CollateralSecurityInfoRow => ({
  row_id: generateRowId(),
  isDirty: true,
  isAdded: true,
});

export const CollateralSecurityDetailsTable: React.FC<CollateralSecurityDetailsTableProps> = () => {
  const dispatch = useAppDispatch();

  const rows = useAppSelector(selectFMR1DraftCollateralInfoRows);
  const sortedRows = useMemo(() => sortRowsBySortOrder(rows), [rows]);

  const handleAddRow = useCallback((rowIndex: number) => {
    const prevRow = sortedRows[rowIndex];
    const prevSort = prevRow?.sort_order;
    const nextSort = sortedRows[rowIndex + 1]?.sort_order;
    const newRow: CollateralSecurityInfoRow = {
      ...buildEmptyRow(),
      sort_order: getInsertedSortOrder(prevSort, nextSort),
      sno: getInsertedSno(prevRow),
    };

    const updates = getSnoUpdates(sortedRows, rowIndex + 1, +1);
    updates.forEach((u) => {
      dispatch(updateCollateralInfoRow({ id: u.id, changes: { sno: u.newSno } }));
    });

    dispatch(addCollateralInfoRow({ row: newRow }));
  }, [dispatch, sortedRows]);

  const handleDeleteRow = useCallback(async (rowIndex: number) => {
    const deletedRow = sortedRows[rowIndex];
    if (!deletedRow) return;

    const updates = getSnoUpdates(sortedRows, rowIndex + 1, -1);
    updates.forEach((u) => {
      dispatch(updateCollateralInfoRow({ id: u.id, changes: { sno: u.newSno } }));
    });

    dispatch(deleteCollateralInfoRow({ id: deletedRow.row_id }));

    if (deletedRow.isAdded) return;
  }, [dispatch, sortedRows]);

  const handleChange = useCallback(
    (rowIndex: number, value: any, field: keyof CollateralSecurityInfoRow) => {
      const row = sortedRows[rowIndex];
      if (!row) return;
      dispatch(updateCollateralInfoRow({ id: row.row_id, changes: { [field]: value } as any }));
    },
    [dispatch, sortedRows],
  );

  const columns: FMRTableColumn<CollateralSecurityInfoRow>[] = [
    {
      headerName: "Sr. No.",
      field: "sno",
      type: "text" as ColumnType,
      isEditable: false,
      className: "w-[50px]",
      getValue: (row) => row.sno ?? null,
    },

    {
      headerName: "Details of Collateral Security",
      field: "details_of_collateral_security",
      type: "text" as ColumnType,
      isEditable: true,
      getValue: (row) => row.details_of_collateral_security ?? "",
      onChange: (rowIndex: number, value: string) => handleChange(rowIndex, value, "details_of_collateral_security"),
    },

    {
      headerName: "Value of the Security",
      field: "value_of_the_security",
      type: "number" as ColumnType,
      isEditable: true,
      getValue: (row) => row.value_of_the_security ?? null,
      onChange: (rowIndex: number, value: number | null) => handleChange(rowIndex, value, "value_of_the_security"),
    },

    {
      headerName: "Date of Valuation",
      field: "date_of_valuation",
      type: "date" as ColumnType,
      isEditable: true,
      getValue: (row) => row.date_of_valuation ?? null,
      onChange: (rowIndex: number, value: string) => handleChange(rowIndex, value, "date_of_valuation"),
    },

    {
      headerName: "Whether Valid?",
      field: "whether_valid",
      type: "select" as ColumnType,
      isEditable: true,
      options: YES_NO_OPTIONS,
      getValue: (row) => row.whether_valid ?? null,
      onChange: (rowIndex: number, value: string) => handleChange(rowIndex, value, "whether_valid"),
    },

    {
      headerName: "Whether Enforceable?",
      field: "whether_enforceable",
      type: "select" as ColumnType,
      isEditable: true,
      options: YES_NO_OPTIONS,
      getValue: (row) => row.whether_enforceable ?? null,
      onChange: (rowIndex: number, value: string) => handleChange(rowIndex, value, "whether_enforceable"),
    },

    {
      headerName: "Remarks",
      field: "remarks",
      type: "text" as ColumnType,
      isEditable: true,
      getValue: (row) => row.remarks ?? "",
      onChange: (rowIndex: number, value: string) => handleChange(rowIndex, value, "remarks"),
    },
  ];

  return (
    <FMRTable
      columns={columns}
      rows={sortedRows}
      onAdd={handleAddRow}
      onDelete={handleDeleteRow}
      title={"Collateral Security Details"}
    />
  );
};

CollateralSecurityDetailsTable.displayName = "CollateralSecurityDetailsTable";
